import { intersectionWith } from 'lodash'
import { flow } from 'lodash/fp'
import { createSelector } from 'reselect'
import { notNullOrEmpty } from 'shared'
import { datahubApi } from 'store/api/datahub'
import { call, put, takeLatest } from 'typed-redux-saga'
import { ActionType, createAction, createReducer } from 'typesafe-actions'
import { AppState } from '..'
import { getRandomCapitalLetter, getRandomName } from '../../api/random'
import { rdotUserActions } from '../user/rdotUser'
import { getEnableDataMaskingPreference } from '../user/selectors'

export interface IDomainItem {
  id?: string
  ClientAdvisorID?: string
  ClientAdvisor?: string
  ClientAdvisorTeam?: string
  AdvisorKPI?: IAdvisorKPI
  BusinessSegment?: string
}

export interface IAdvisorKPI {
  poolMemberKPI?: IPoolMemberKPI[]
}

export interface IPoolMemberKPI {
  ClientAdvisorKPI?: IClientAdvisorKPI[]
  Custodian?: string
  CustodianNo?: string
  RCMEmail?: string
}

export interface IClientAdvisorKPI {
  RepNoPercentage?: string
  AlternateInvestment?: number
  AumBrokerage?: number
  AumManaged?: number
  AumTotal?: number
  LoanOutstanding?: number
  NetNewMoney?: number
  TOA?: number
  TradeBrokerageCount?: number
  TradeCommision?: number
  TradeManagedCount?: number
  cashAvlToTrade?: number
  cashInFlow?: number
  cashOutFlow?: number
}

const DOMAIN_REQUEST = '@context/@domain/DOMAIN_REQUEST'
const DOMAIN_SUCCESS = '@context/@domain/DOMAIN_SUCCESS'
const DOMAIN_ERROR = '@context/@domain/DOMAIN_ERROR'

const domainContextRequestActions = {
  request: createAction(DOMAIN_REQUEST)(),
  success: createAction(DOMAIN_SUCCESS)<IDomainItem[]>(),
  failure: createAction(DOMAIN_ERROR)<Error>()
}

const SET_SELECTED_DOMAIN = '@context/@domain/SET_SELECTED_DOMAIN'
export const domainContextActions = {
  setSelectedDomain: createAction(SET_SELECTED_DOMAIN)<string[]>(),
  setSelectedDomainReps: createAction(SET_SELECTED_DOMAIN)<string[]>()
}

type DomainContextActionTypes =
  | ActionType<typeof domainContextRequestActions>
  | ActionType<typeof domainContextActions>

interface IDomainContextState {
  items?: IDomainItem[]
  loading?: boolean
  error?: Error
  selected: string[]
}

const initialState: IDomainContextState = {
  loading: true,
  selected: []
}

export const domainContextReducer = createReducer<
  IDomainContextState,
  DomainContextActionTypes
>(initialState)
  .handleAction(domainContextRequestActions.request, () => ({
    ...initialState,
    loading: true
  }))
  .handleAction(domainContextRequestActions.success, (state, action) => ({
    ...state,
    items: action.payload,
    loading: false
  }))
  .handleAction(domainContextRequestActions.failure, (state, action) => ({
    ...state,
    error: action.payload,
    loading: false
  }))
  .handleAction(domainContextActions.setSelectedDomain, (state, action) => ({
    ...state,
    selected: action.payload
  }))

const getDomainContextState = (state: AppState) => state.context.domain
export const getIsDomainContextLoading = flow(
  getDomainContextState,
  (x) => x.loading
)
export const getDomainContextError = flow(getDomainContextState, (x) => x.error)
const getDomainContextItemsInternal = flow(
  getDomainContextState,
  (x) => x.items
)
export const getDomainContextItems = createSelector(
  [getDomainContextItemsInternal, getEnableDataMaskingPreference],
  (items, shouldMask) => {
    if (!shouldMask) {
      return items
    }
    const masked = items?.map((x) => {
      const advisorId = getRandomCapitalLetter() + x.ClientAdvisorID?.slice(-2)
      return {
        ...x,
        ClientAdvisor: getRandomName(),
        ClientAdvisorID: advisorId,
        ClientAdvisorTeam: getRandomName() + ' Team'
      }
    })

    return masked
  }
)
export const getSelectedDomainContextItems = createSelector(
  getDomainContextState,
  ({ selected, items }) =>
    intersectionWith(items, selected, (item, id) => item?.id === id)
)
export const getSelectedDomainContextRepCodes = createSelector(
  [getDomainContextState],
  ({ selected }) => selected
)
export const getIsAllDomainContextItemsSelected = createSelector(
  [getDomainContextState],
  ({ selected, items }) => selected.length === items?.length
)
export const getDomainContextRepCodes = createSelector(
  [getDomainContextItemsInternal],
  (items) => items?.map((x) => x.id).filter(notNullOrEmpty) || []
)

function* saga_getDomain() {
  const action = datahubApi.endpoints.getDomain.initiate()
  const promise = yield* put(action)
  promise.unsubscribe()
  return yield* call(promise.unwrap)
}

const fetchDomain = function* () {
  try {
    const domain = yield* call(saga_getDomain)

    if (!domain) {
      yield put(
        domainContextRequestActions.failure(new Error('Domain not found'))
      )
      return
    }

    yield put(domainContextRequestActions.success(domain))
  } catch (e: any) {
    yield put(domainContextRequestActions.failure(e))
  }
}

export const domainSagas = [
  () => takeLatest(domainContextRequestActions.request, fetchDomain),
  () =>
    takeLatest(rdotUserActions.loginSuccess, function* () {
      yield put(domainContextRequestActions.request())
    })
]
